import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
  createSessionToken,
  fetchPlaceDetails,
  fetchPlaceSuggestions,
  parsePlaceAddressComponents,
} from "../../../helpers/placesApi";
import "./addressAutocomplete.css";

const DEBOUNCE_MS = 280;

/**
 * Address input with Google Places API (New) REST suggestions.
 * Selecting a suggestion calls onPlaceSelect with parsed street/city/state/zip.
 * The field stays editable afterward.
 */
export default function AddressAutocomplete({
  label = "Address",
  name = "address1",
  value = "",
  onChange,
  onPlaceSelect,
  placeholder = "Start typing an address",
  required = false,
  error = false,
  className = "",
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(createSessionToken());
  const skipNextFetchRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchSuggestions = async (input) => {
    if (!String(input || "").trim()) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const predictions = await fetchPlaceSuggestions(
        input,
        sessionTokenRef.current
      );

      if (requestId !== requestIdRef.current) return;

      if (!predictions.length) {
        setSuggestions([]);
        setOpen(false);
        return;
      }

      setSuggestions(predictions);
      setOpen(true);
      setActiveIndex(-1);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Places autocomplete error:", err);
      setSuggestions([]);
      setOpen(false);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (event) => {
    onChange?.(event);

    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    const nextValue = event.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(nextValue);
    }, DEBOUNCE_MS);
  };

  const applyPrediction = async (prediction) => {
    if (!prediction?.placeId) return;

    try {
      setLoading(true);
      const place = await fetchPlaceDetails(
        prediction.placeId,
        sessionTokenRef.current
      );

      // Session ends after place details — start a new one for the next search
      sessionTokenRef.current = createSessionToken();

      const parsed = parsePlaceAddressComponents(place.addressComponents || []);
      const displayName =
        typeof place.displayName === "string"
          ? place.displayName
          : place.displayName?.text || "";
      const street =
        parsed.address1 ||
        displayName ||
        prediction.mainText ||
        place.formattedAddress ||
        "";

      skipNextFetchRef.current = true;
      onChange?.({
        target: { name, value: street },
      });

      onPlaceSelect?.({
        address1: street,
        city: parsed.city || "",
        state: parsed.state || "",
        zip: parsed.zip || "",
        country: parsed.country || "",
      });

      setSuggestions([]);
      setOpen(false);
      setActiveIndex(-1);
    } catch (err) {
      console.error("Place details error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (!open || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      applyPrediction(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <Form.Group className={`mb-3 address-autocomplete ${className}`.trim()} ref={wrapperRef}>
      {label ? (
        <Form.Label className="cmn_label">
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      ) : null}
      <div className="address-autocomplete__field">
        <div className="address-autocomplete__input-wrap">
          <Form.Control
            className={`${disabled ? "disabled_input" : "cmn_input"} ${
              error ? "border border-danger" : ""
            }`}
            type="text"
            name={name}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) setOpen(true);
            }}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            readOnly={disabled}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls="address-autocomplete-list"
          />
          {loading ? (
            <div className="address-autocomplete__loading">Searching…</div>
          ) : null}
        </div>
        {open && suggestions.length > 0 && (
          <ul
            id="address-autocomplete-list"
            className="address-autocomplete__list"
            role="listbox"
          >
            {suggestions.map((prediction, index) => (
              <li
                key={prediction.placeId}
                role="option"
                aria-selected={index === activeIndex}
              >
                <button
                  type="button"
                  className={`address-autocomplete__option ${
                    index === activeIndex ? "is-active" : ""
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyPrediction(prediction)}
                >
                  <span className="address-autocomplete__main">
                    {prediction.mainText}
                  </span>
                  {prediction.secondaryText ? (
                    <span className="address-autocomplete__secondary">
                      {prediction.secondaryText}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Form.Group>
  );
}
